import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export interface FacadePoint {
  x: number;
  y: number;
}

// A clickable floor region drawn over the facade image. Points are stored in
// the image's natural pixel coordinates so the overlay scales with the image.
export interface FacadeArea {
  id: string;
  entrance: string; // entrance id (matches BuildingConfig entrance ids)
  floor: number;
  points: FacadePoint[];
  label?: string;
}

// One facade image with its own clickable areas. A project can have several —
// different sides of a building, or separate buildings in a complex.
export interface Facade {
  id: string;
  name: string;
  imageUrl: string;
  areas: FacadeArea[];
}

export interface FacadeConfig {
  id: string;
  facades: Facade[];
  updatedAt?: Date;
}

const defaultFacade: FacadeConfig = {
  id: 'facade',
  facades: [],
};

// Documents written before multiple facades existed hold a single imageUrl and
// areas at the top level; read them as the first facade.
const normalise = (data: Record<string, unknown>): Facade[] => {
  if (Array.isArray(data.facades)) {
    return (data.facades as Facade[])
      .filter((f) => f && typeof f.imageUrl === 'string')
      .map((f, index) => ({
        id: f.id || `facade-${index}`,
        name: f.name || `Фасада ${index + 1}`,
        imageUrl: f.imageUrl,
        areas: Array.isArray(f.areas) ? f.areas : [],
      }));
  }
  if (typeof data.imageUrl === 'string' && data.imageUrl) {
    return [
      {
        id: 'facade-1',
        name: 'Фасада',
        imageUrl: data.imageUrl,
        areas: Array.isArray(data.areas) ? (data.areas as FacadeArea[]) : [],
      },
    ];
  }
  return [];
};

export const useFacade = () => {
  const [facade, setFacade] = useState<FacadeConfig>(defaultFacade);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFacade = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'content', 'facade');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setFacade({
          id: docSnap.id,
          facades: normalise(data),
          updatedAt: data.updatedAt?.toDate(),
        });
      } else {
        setFacade(defaultFacade);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching facade:', err);
      setError('Грешка при зареждане на фасадата');
      setFacade(defaultFacade);
    } finally {
      setLoading(false);
    }
  };

  const saveFacade = async (newFacade: Partial<FacadeConfig>) => {
    try {
      const docRef = doc(db, 'content', 'facade');
      const facades = newFacade.facades ?? facade.facades;
      await setDoc(docRef, { facades, updatedAt: serverTimestamp() });
      setFacade((prev) => ({ ...prev, facades, updatedAt: new Date() }));
      return true;
    } catch (err) {
      console.error('Error saving facade:', err);
      setError('Грешка при запазване на фасадата');
      return false;
    }
  };

  useEffect(() => {
    fetchFacade();
  }, []);

  return {
    facade,
    loading,
    error,
    saveFacade,
    refreshFacade: fetchFacade,
  };
};
